import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from 'react-query'
import { agentService, apiService, teamService, settingsService } from '../services/api'
import { ArrowLeft, Download, Rocket, Loader2, Key, Plus, Users } from 'lucide-react'
import { useAuthStore } from '../stores/authStore'

type DeploymentMethod = 'direct' | 'download'

export function DeployAgent() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user } = useAuthStore()
  
  const [selectedEngine, setSelectedEngine] = useState<string>('')
  const [selectedAgentId, setSelectedAgentId] = useState<string>('')
  
  // * Fetch default engine
  const { data: defaultEngine } = useQuery(
    'defaultEngine',
    () => settingsService.getSetting('default_agent_engine'),
    {
      initialData: 'agent-python-full' // * Default fallback
    }
  )
  
  // * Set default engine when default engine is loaded
  useEffect(() => {
    if (defaultEngine && !selectedEngine) {
      setSelectedEngine(defaultEngine)
    }
  }, [defaultEngine, selectedEngine])

  // * Pre-select agent if provided in URL
  useEffect(() => {
    const agentId = searchParams.get('agentId')
    if (agentId) {
      setSelectedAgentId(agentId)
    }
  }, [searchParams])
  const [deploymentMethod, setDeploymentMethod] = useState<DeploymentMethod>('direct')
  const [customCoreUrl, setCustomCoreUrl] = useState<string>('https://core.atf.therapia.solutions/')
  const [isDeploying, setIsDeploying] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  
  // * Team management
  const [newTeamInstanceName, setNewTeamInstanceName] = useState<string>('')
  const [selectedTeamInstanceId, setSelectedTeamInstanceId] = useState<string>('')
  const [selectedRoleId, setSelectedRoleId] = useState<string>('')
  const [isCreatingTeamInstance, setIsCreatingTeamInstance] = useState(false)
  
  // * API Keys management
  // * Store key IDs (not decrypted values) - keys will be fetched server-side when needed
  // * Structure: { "provider:keyType": keyId } to allow one key per category per provider
  const [selectedApiKeyIds, setSelectedApiKeyIds] = useState<Record<string, string>>({})
  const [showAddKeyForm, setShowAddKeyForm] = useState(false)
  const [newKey, setNewKey] = useState({ provider: 'anthropic', keyType: 'API_KEY', key: '' })
  
  // * Fetch available API keys
  const { data: apiKeys } = useQuery('apiKeys', apiService.getApiKeys)

  // * Fetch available agents (models)
  const { data: agents, isLoading: agentsLoading } = useQuery(
    'agents',
    agentService.getAllAgents
  )

  // * Fetch team templates
  const { data: teamTemplates } = useQuery('teamTemplates', teamService.getAllTeamTemplates)

  // * Fetch team instances
  const { data: teamInstances } = useQuery('teamInstances', teamService.getAllTeamInstances)

  // * Get selected team instance
  const selectedTeamInstance = teamInstances?.find(t => t.id === selectedTeamInstanceId)
  
  // * Get selected team template from the instance's teamTemplateId
  const selectedTeamTemplate = selectedTeamInstance 
    ? teamTemplates?.find(t => t.id === selectedTeamInstance.teamTemplateId)
    : null

  // * Get available roles for selected team instance
  const availableRoles = selectedTeamInstance && selectedTeamTemplate
    ? selectedTeamTemplate.roles.filter(role => {
        // * Check if role is already occupied (if unique)
        if (role.isUnique) {
          const occupied = selectedTeamInstance.agents?.some(a => a.roleId === role.id)
          return !occupied
        }
        return true
      })
    : []

  // * Available engines (for now, only Python)
  const engines = [
    { id: 'agent-python-full', name: 'Agent Python Full', description: 'Agent Python complet (LLMs + web + communications)' },
    { id: 'agent-python-modular', name: 'Agent Python Modular', description: 'Agent Python modulaire (LLMs + web + comms en modules)' }
  ]

  const handleDirectDeploy = async () => {
    if (!selectedAgentId) {
      alert('Veuillez sélectionner un modèle d\'agent')
      return
    }

    // * Validation: if team is selected, role is required
    if (selectedTeamInstanceId && !selectedRoleId) {
      alert('Veuillez sélectionner un rôle dans l\'équipe')
      return
    }

    setIsDeploying(true)
    console.log('🚀 Démarrage du déploiement...', { agentId: selectedAgentId, engine: selectedEngine })
    
    try {
      console.log('📡 Envoi de la requête de déploiement...')
      // * Send all selected keys in "provider:keyType" format
      // * Backend will map them to the expected config key names
      const apiKeyProviders = Object.keys(selectedApiKeyIds).length > 0 
        ? selectedApiKeyIds
        : undefined

      const response = await agentService.deployAgentInstance(selectedAgentId, {
        engine: selectedEngine || undefined, // * If empty, backend will use default engine
        coreUrl: undefined, // * Use default Core URL
        apiKeyIds: apiKeyProviders, // * Send key IDs, not decrypted values
        teamInstanceId: selectedTeamInstanceId || undefined,
        roleId: selectedRoleId || undefined
      })
      
      console.log('✅ Déploiement réussi:', response)
      alert(`Agent déployé avec succès !\nInstance ID: ${response.instanceId}`)
      navigate('/monitoring')
    } catch (error: any) {
      console.error('❌ Erreur lors du déploiement:', error)
      console.error('📋 Détails de l\'erreur:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText,
        config: error.config
      })
      
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          error.message || 
                          'Erreur inconnue'
      const errorDetails = error.response?.data?.details ? 
                          `\n\nDétails: ${JSON.stringify(error.response.data.details, null, 2)}` : ''
      
      alert(`Erreur lors du déploiement (${error.response?.status || 'N/A'}):\n${errorMessage}${errorDetails}`)
    } finally {
      setIsDeploying(false)
    }
  }

  const handleDownloadArchive = async () => {
    if (!selectedAgentId) {
      alert('Veuillez sélectionner un modèle d\'agent')
      return
    }

    if (deploymentMethod === 'download' && !customCoreUrl.trim()) {
      alert('Veuillez saisir l\'URL du Core ATF')
      return
    }

      setIsDownloading(true)
    try {
      // * Send all selected keys in "provider:keyType" format
      // * Backend will map them to the expected config key names
      const apiKeyProviders = Object.keys(selectedApiKeyIds).length > 0 
        ? selectedApiKeyIds
        : undefined

      const response = await agentService.downloadAgentDeploymentPackage(selectedAgentId, {
        engine: selectedEngine,
        coreUrl: customCoreUrl.trim() || undefined,
        apiKeyIds: apiKeyProviders // * Send key IDs, not decrypted values
      })
      
      // * Create download link
      const blob = new Blob([response], { type: 'application/zip' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `agent-deployment-${selectedAgentId}-${Date.now()}.zip`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
      
      alert('Archive téléchargée avec succès !')
    } catch (error: any) {
      console.error('Erreur lors du téléchargement:', error)
      alert(`Erreur lors du téléchargement: ${error.message || 'Erreur inconnue'}`)
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <div className="page-content relative min-h-screen">
      {/* Image de robot en arrière-plan */}
      <div className="background-image">
        <img src="/logo.jpg" alt="Robot background" />
      </div>
      
      {/* Contenu au premier plan */}
      <div className="content-wrapper">
        <div className="space-y-6">
          {/* En-tête */}
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/agents')}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Retour</span>
            </button>
            <h1 className="text-3xl font-extrabold tracking-tight tab_title">Déployer un agent</h1>
          </div>

          <div className="card space-y-6">
            {/* Étape 1: Sélection du moteur */}
            <div>
              <h2 className="text-xl font-semibold mb-4">1. Choisir un moteur</h2>
              <div className="space-y-3">
                {engines.map(engine => (
                  <label
                    key={engine.id}
                    className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                      selectedEngine === engine.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="engine"
                      value={engine.id}
                      checked={selectedEngine === engine.id}
                      onChange={(e) => setSelectedEngine(e.target.value)}
                      className="mt-1 mr-3"
                    />
                    <div>
                      <div className="font-medium">{engine.name}</div>
                      <div className="text-sm text-gray-600">{engine.description}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Étape 2: Sélection du modèle d'agent */}
            <div>
              <h2 className="text-xl font-semibold mb-4">2. Choisir un modèle d'agent</h2>
              {agentsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              ) : (
                <select
                  value={selectedAgentId}
                  onChange={(e) => setSelectedAgentId(e.target.value)}
                  className="form-input w-full"
                  required
                >
                  <option value="">-- Sélectionner un modèle d'agent --</option>
                  {agents?.map(agent => (
                    <option key={agent.id} value={agent.id}>
                      {agent.name} (v{agent.version})
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Étape 3: Assignation à une équipe (optionnel) */}
            <div>
              <h2 className="text-xl font-semibold mb-4">3. Assigner à une équipe (optionnel)</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Instance d'équipe</label>
                  <select
                    value={selectedTeamInstanceId}
                    onChange={(e) => {
                      setSelectedTeamInstanceId(e.target.value)
                      setSelectedRoleId('') // * Reset role when team changes
                    }}
                    className="form-input w-full"
                  >
                    <option value="">-- Aucune équipe --</option>
                    {teamInstances?.map(instance => (
                      <option key={instance.id} value={instance.id}>
                        {instance.teamTemplateName} {instance.name ? `- ${instance.name}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                {selectedTeamInstanceId && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Rôle dans l'équipe *</label>
                    {availableRoles.length === 0 ? (
                      <p className="text-sm text-secondary">
                        Aucun rôle disponible. Tous les rôles sont peut-être déjà occupés.
                      </p>
                    ) : (
                      <select
                        value={selectedRoleId}
                        onChange={(e) => setSelectedRoleId(e.target.value)}
                        className="form-input w-full"
                        required
                      >
                        <option value="">-- Sélectionner un rôle --</option>
                        {availableRoles.map(role => (
                          <option key={role.id} value={role.id}>
                            {role.name} {role.isUnique && '(Unique)'}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Étape 4: Clés API */}
            <div>
              <h2 className="text-xl font-semibold mb-4">4. Clés API</h2>
              <p className="text-sm text-gray-600 mb-4">
                Sélectionnez ou ajoutez des clés API pour les fournisseurs LLM. Ces clés seront incluses dans la configuration de l'agent.
                Les clés enregistrées ne peuvent pas être affichées en clair pour des raisons de sécurité.
              </p>
              
              {/* Liste des clés disponibles */}
              {apiKeys && apiKeys.length > 0 && (
                <div className="space-y-4 mb-4">
                  <label className="block text-sm font-medium text-primary mb-2">Clés enregistrées</label>
                  {Array.from(new Set(apiKeys.map(k => k.provider))).map((provider) => {
                    const providerKeys = apiKeys.filter(k => k.provider === provider)
                    // * Group keys by keyType
                    const keysByType = providerKeys.reduce((acc, key) => {
                      if (!acc[key.keyType]) {
                        acc[key.keyType] = []
                      }
                      acc[key.keyType].push(key)
                      return acc
                    }, {} as Record<string, typeof providerKeys>)
                    
                    return (
                      <div key={provider} className="border border-gray-200 rounded-lg p-3 space-y-3">
                        <div className="font-medium text-sm text-primary">{provider}</div>
                        {Object.entries(keysByType).map(([keyType, keys]) => {
                          const compositeKey = `${provider}:${keyType}`
                          const selectedKeyId = selectedApiKeyIds[compositeKey]
                          return (
                            <div key={keyType} className="pl-4 border-l-2 border-gray-200">
                              <div className="text-xs font-medium text-secondary mb-2">{keyType}</div>
                              <div className="flex flex-wrap items-center gap-2">
                                <label className="flex items-center gap-2">
                                  <input
                                    type="radio"
                                    name={`apiKey_${compositeKey}`}
                                    value=""
                                    checked={!selectedKeyId}
                                    onChange={() => {
                                      const newKeys = { ...selectedApiKeyIds }
                                      delete newKeys[compositeKey]
                                      setSelectedApiKeyIds(newKeys)
                                    }}
                                    className="mr-2"
                                  />
                                  <span className="text-xs text-gray-500">Aucune</span>
                                </label>
                                {keys.map((key) => (
                                  <label key={key.id} className="flex items-center gap-2">
                                    <input
                                      type="radio"
                                      name={`apiKey_${compositeKey}`}
                                      value={key.id}
                                      checked={selectedKeyId === key.id}
                                      onChange={() => {
                                        setSelectedApiKeyIds({ ...selectedApiKeyIds, [compositeKey]: key.id })
                                      }}
                                      className="mr-2"
                                    />
                                    <span className="text-sm">{key.keyPreview}</span>
                                    {key.description && (
                                      <span className="text-xs text-gray-500">({key.description})</span>
                                    )}
                                  </label>
                                ))}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )
                  })}
                </div>
              )}

              {/* Formulaire d'ajout rapide */}
              {showAddKeyForm ? (
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-primary mb-1">Fournisseur</label>
                    <select
                      value={newKey.provider}
                      onChange={(e) => setNewKey({ ...newKey, provider: e.target.value })}
                      className="input"
                    >
                      <option value="mistral">Mistral AI</option>
                      <option value="anthropic">Anthropic (Claude)</option>
                      <option value="openai">OpenAI</option>
                      <option value="assembly">Assembly.ai</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-primary mb-1">Type de clé</label>
                    <select
                      value={newKey.keyType}
                      onChange={(e) => setNewKey({ ...newKey, keyType: e.target.value })}
                      className="input"
                    >
                      <option value="API_KEY">Clé API</option>
                      <option value="SECRET">Secret</option>
                      <option value="TOKEN">Token</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-primary mb-1">Clé API</label>
                    <input
                      type="password"
                      value={newKey.key}
                      onChange={(e) => setNewKey({ ...newKey, key: e.target.value })}
                      placeholder="sk-..."
                      className="input w-full"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={async () => {
                        if (!newKey.key) {
                          alert('Veuillez saisir une clé')
                          return
                        }
                        // * For manual keys, we'll send the key directly (temporary, not stored)
                        const compositeKey = `${newKey.provider}:${newKey.keyType}`
                        setSelectedApiKeyIds({ ...selectedApiKeyIds, [compositeKey]: `manual:${newKey.key}` })
                        setNewKey({ provider: 'anthropic', keyType: 'API_KEY', key: '' })
                        setShowAddKeyForm(false)
                      }}
                      className="button button--primary text-sm"
                    >
                      Utiliser cette clé
                    </button>
                    <button
                      onClick={() => {
                        setShowAddKeyForm(false)
                        setNewKey({ provider: 'anthropic', keyType: 'API_KEY', key: '' })
                      }}
                      className="button button--secondary text-sm"
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowAddKeyForm(true)}
                  className="button button--secondary flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  <span>Ajouter une clé manuellement</span>
                </button>
              )}

              {/* Clés sélectionnées */}
              {Object.keys(selectedApiKeyIds).length > 0 && (
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="text-sm font-medium text-primary mb-2">Clés sélectionnées :</div>
                  <div className="space-y-1">
                    {Object.entries(selectedApiKeyIds).map(([compositeKey, keyId]) => {
                      const [provider, keyType] = compositeKey.split(':')
                      const key = apiKeys?.find(k => k.id === keyId)
                      const isManual = keyId.startsWith('manual:')
                      return (
                        <div key={compositeKey} className="text-sm text-secondary flex items-center gap-2">
                          <Key className="w-4 h-4" />
                          <span className="font-medium">{provider}</span>
                          <span className="text-xs text-gray-500">({keyType}):</span>
                          {isManual ? (
                            <span className="font-mono text-xs">Clé manuelle (non stockée)</span>
                          ) : (
                            <span className="font-mono">{key?.keyPreview || 'Clé sélectionnée'}</span>
                          )}
                          <button
                            onClick={() => {
                              const newKeys = { ...selectedApiKeyIds }
                              delete newKeys[compositeKey]
                              setSelectedApiKeyIds(newKeys)
                            }}
                            className="text-red-600 hover:text-red-800 ml-2"
                          >
                            ×
                          </button>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Étape 4: Méthode de déploiement */}
            <div>
              <h2 className="text-xl font-semibold mb-4">4. Choisir la méthode de déploiement</h2>
              <div className="space-y-4">
                {/* Option 1: Déploiement direct */}
                <label
                  className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                    deploymentMethod === 'direct'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="deploymentMethod"
                    value="direct"
                    checked={deploymentMethod === 'direct'}
                    onChange={(e) => setDeploymentMethod(e.target.value as DeploymentMethod)}
                    className="mt-1 mr-3"
                  />
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <Rocket className="w-5 h-5 text-blue-600" />
                      <div className="font-medium">Déploiement direct</div>
                    </div>
                    <div className="text-sm text-gray-600">
                      Lance une instance de l'agent directement dans le Core ATF disponible sur ce serveur.
                      L'agent s'enregistrera automatiquement et sera accessible via les routes Traefik.
                    </div>
                  </div>
                </label>

                {/* Option 2: Téléchargement d'archive */}
                <label
                  className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                    deploymentMethod === 'download'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="deploymentMethod"
                    value="download"
                    checked={deploymentMethod === 'download'}
                    onChange={(e) => setDeploymentMethod(e.target.value as DeploymentMethod)}
                    className="mt-1 mr-3"
                  />
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <Download className="w-5 h-5 text-blue-600" />
                      <div className="font-medium">Télécharger une archive de déploiement</div>
                    </div>
                    <div className="text-sm text-gray-600 mb-3">
                      Télécharge une archive ZIP contenant le moteur complet, les fichiers de configuration
                      et les scripts d'installation pour déployer l'agent sur un autre serveur.
                    </div>
                    {deploymentMethod === 'download' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          URL du Core ATF (pour déploiement sur un autre serveur)
                        </label>
                        <input
                          type="url"
                          value={customCoreUrl}
                          onChange={(e) => setCustomCoreUrl(e.target.value)}
                          placeholder="https://core.atf.example.com"
                          className="form-input w-full"
                        />
                        <div className="text-xs text-gray-500 mt-1">
                          Si vide, l'URL devra être configurée manuellement dans les fichiers de configuration.
                        </div>
                      </div>
                    )}
                  </div>
                </label>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-4 pt-4 border-t">
              <button
                type="button"
                onClick={() => navigate('/agents')}
                className="button button--secondary"
              >
                Annuler
              </button>
              {deploymentMethod === 'direct' ? (
                <button
                  type="button"
                  onClick={handleDirectDeploy}
                  disabled={!selectedAgentId || isDeploying}
                  className="button button--primary flex items-center space-x-2"
                >
                  {isDeploying ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Déploiement en cours...</span>
                    </>
                  ) : (
                    <>
                      <Rocket className="w-5 h-5" />
                      <span>Déployer maintenant</span>
                    </>
                  )}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleDownloadArchive}
                  disabled={!selectedAgentId || isDownloading}
                  className="button button--primary flex items-center space-x-2"
                >
                  {isDownloading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Téléchargement en cours...</span>
                    </>
                  ) : (
                    <>
                      <Download className="w-5 h-5" />
                      <span>Télécharger l'archive</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

